import * as PIXI from 'pixi.js-legacy';
import isEqual from 'lodash/isEqual';
import { borderLines, isDefined, perpendicular, toPixel } from '../utils';
import HorizontalLine from './HorizontalLine';
import VerticalLine from './VerticalLine';
import ArcLine from './ArcLine';
import SortGrid from './Sort';

export default class Grid {
  container;
  sequence;
  duration;
  onlyStart = false;
  onlyEnd = false;
  highlightInfo = { type: 'horizontal', index: 0, line: null };
  clickable = false;

  /**
   * Grid Setup
   * @constructor
   * @param {PIXI.Application | PIXI.Container} app
   * @param {number} width
   * @param {number} height
   * @param {number} thickness
   * @param {number} lineColor
   */
  constructor(app, width, height, thickness, lineColor = 0) {
    this.thickness = thickness;
    this.lineColor = lineColor;

    this.horizontalLines = [];
    this.horizontalInterLines = [];
    this.verticalLines = [];
    this.verticalInterLines = [];
    this.circleLines = [];
    this.circleInterLines = [];

    // PIXI application setup
    this.width = width;
    this.height = height;
    this.app = app;

    this.containerSetup();
  }

  containerSetup() {
    // Containers to group objects in canvas
    this.container = new PIXI.Container();
    this.container.zIndex = 1;
    this.container.interactiveChildren = false;
    this.sorting = new SortGrid();

    this.highlightGraphic = new PIXI.Graphics();

    if (this.app.stage) {
      this.app.stage.interactiveChildren = false;
      this.app.stage.addChild(this.container);
      this.app.stage.addChild(this.highlightGraphic);
      this.app.ticker.maxFPS = 60;
    } else {
      this.app.addChild(this.container);
      this.app.addChild(this.highlightGraphic);
    }
  }

  destroy() {
    this.removeLines(this.horizontalLines);
    this.removeLines(this.verticalLines);
    this.removeLines(this.circleLines);
    this.highlightGraphic.destroy();
    this.container.destroy(true);
  }

  makeClickable() {
    this.clickable = true;
  }

  onClickCallback(line) {
    this.highlightLine(line.type, null, line);
  }

  async draw(
    sequence,
    delay,
    onlyStart = false,
    onlyEnd = false,
    borderIncluded = false,
    delayDelete = true
  ) {
    this.sequence = sequence;
    this.duration = sequence.duration;
    this.onlyStart = onlyStart;
    this.onlyEnd = onlyEnd;
    this.delayDelete = delayDelete;

    const drawBorder = isDefined(sequence.drawBorder)
      ? sequence.drawBorder
      : true; // Defaults to true

    this.hDelay =
      sequence.delayHorizontal === undefined ? true : sequence.delayHorizontal;
    this.vDelay =
      sequence.delayVertical === undefined ? true : sequence.delayVertical;
    this.hDelayDirection =
      sequence.horizontalDelayDirection === undefined
        ? 1
        : sequence.horizontalDelayDirection;
    this.vDelayDirection =
      sequence.verticalDelayDirection === undefined
        ? 1
        : sequence.verticalDelayDirection;

    if (drawBorder && !borderIncluded) {
      this.sequence.vertical.push(
        ...borderLines(sequence.insetBorder, 'vertical'),
      );
      this.sequence.horizontal.push(
        ...borderLines(sequence.insetBorder, 'horizontal'),
      );
    }

    this.sorting.setLines(this.sequence.vertical, this.sequence.horizontal);
    this.sortedLines = this.getLinesByOrder();

    return new Promise(resolve => {
      setTimeout(async () => {
        await Promise.all([
          this.drawHorizontal(),
          this.drawVertical(),
          this.drawCircle(),
        ]);
        resolve();
      }, delay);
    });
  }

  reverse() {
    const sequence = this.sequence;
    const { horizontal, vertical } = sequence;
    const lines = [...horizontal, ...vertical];
    lines.forEach(l => {
      if (l.endPosition) {
        const tmp = l.startPosition;
        l.startPosition = l.endPosition;
        l.endPosition = tmp;
      }
      l.drawFull = !l.drawFull;

      if (!isNaN(l.drawDirection)) {
        l.drawDirection *= -1;
      }
      // TODO finish
      if (l.drawDirection === -1 && !l.drawFull) {
        const {
          drawFromLine,
          drawFromPosition,
          drawToLine,
          drawToPosition,
        } = l;
      }
    });
  }

  removeLines(container) {
    container.forEach(line => line.remove());
  }

  highlight() {
    try {
      if (
        !isDefined(this.highlightInfo.type) ||
        (!isDefined(this.highlightInfo.index) &&
          !isDefined(this.highlightInfo.line))
      ) {
        this.highlightGraphic.clear();
        this.clearHighlightTicker();
        return;
      }

      let line = this.highlightInfo.line;

      if (!isDefined(line)) {
        const { type, index } = this.highlightInfo;
        const lines = this[`${type}Lines`];
        line =
          typeof index === 'string'
            ? lines.find(l => l.instructions.name === index)
            : lines[index];
      }

      if (line) {
        this.highlightGraphic.clear();
        this.highlightGraphic.lineStyle({
          width: this.thickness + 1,
          color: 0xff0000,
        });
        const points = line.line.currentPath
          ? line.line.currentPath.points
          : line.line.geometry.points;
        this.highlightGraphic.drawPolygon(points);
        this.highlightGraphic.x = line.line.x;
        this.highlightGraphic.y = line.line.y;
      }
    } catch (e) {
      console.error(e);
    }
  }

  highlightLine(type, index, line = null) {
    this.clearHighlightTicker();
    this.highlightInfo = { type, index, line };
    this.app.ticker.add(this.highlight, this);
  }

  clearHighlightTicker() {
    this.app.ticker.remove(this.highlight, this);
  }

  newLine(
    type,
    startPosition,
    coordinate,
    startColor,
    startAlpha,
    instructions,
    maxLength,
  ) {
    const line =
      type === 'horizontal'
        ? new HorizontalLine(
            this.container,
            {
              x: toPixel(coordinate, this.width),
              y: toPixel(startPosition, this.height),
            },
            maxLength,
            instructions,
            this.thickness,
            startAlpha,
            startColor || this.lineColor,
            this.duration,
          )
        : new VerticalLine(
            this.container,
            {
              x: toPixel(startPosition, this.width),
              y: toPixel(coordinate, this.height),
            },
            maxLength,
            instructions,
            this.thickness,
            startAlpha,
            startColor || this.lineColor,
            this.duration,
          );

    if (this.clickable) {
      line.makeClickable(this.onClickCallback);
    }

    return line;
  }

  newArcLine(instructions, container = this.container) {
    const {
      startLinePositionX,
      startLinePositionY,
      startPositionX,
      startPositionY,
      drawFromAngle: startAngle,
      maxAngle,
      collapse,
      clockwise,
      startAlpha,
      startColor,
    } = instructions;

    const { radius, ..._instructions } = instructions;

    let posX = startPositionX;
    let posY = startPositionY;

    if (isDefined(startLinePositionX))
      posX = this.getLineByIndex('vertical', startLinePositionX).startPosition;
    if (isDefined(startLinePositionY))
      posY = this.getLineByIndex('horizontal', startLinePositionY)
        .startPosition;
    return new ArcLine(
      container,
      {
        x: toPixel(posX, this.width),
        y: toPixel(posY, this.height),
        startAngle,
        radius,
        maxAngle,
        collapse,
        clockwise,
      },
      null,
      _instructions,
      this.thickness,
      startAlpha,
      startColor || this.lineColor,
      this.duration,
    );
  }

  async drawLines(type) {
    const lineSequence = this.sequence[type] || [];
    const lineContainer = [];
    const perpendicularType = type === 'horizontal' ? 'vertical' : 'horizontal';
    const shouldDelay = type === 'horizontal' ? this.hDelay : this.vDelay;
    const shouldDelayPerpendicular =
      type === 'horizontal' ? this.vDelay : this.hDelay;

    const shouldReverse =
      type === 'horizontal' ? this.hDelayDirection : this.vDelayDirection;

    // Setup Lines + add them to container - lines will be in order of end positions
    const lines = this.sorting.sortLines(lineSequence, type).map(l => {
      const {
        drawFromPosition,
        startPosition,
        drawFromLine,
        startAlpha,
        startColor,
        segmentGroup,
        maxLength,
      } = l;
      let coordinate = drawFromPosition;

      if (isDefined(drawFromLine)) {
        const { startPosition: sp } = this.getLineByIndex(
          perpendicularType,
          drawFromLine,
        );
        coordinate = sp;
      }

      // Group of lines
      if (segmentGroup) {
        const { numLines, segmentPositionEnd, segmentLineEnd } = segmentGroup;
        const segLines = numLines;
        let segEnd = segmentPositionEnd;

        if (isDefined(segmentLineEnd)) {
          const { startPosition: sp } = this.getLineByIndex(
            perpendicularType,
            segmentLineEnd,
          );
          segEnd = sp;
        }

        const distance = Math.abs(startPosition - segEnd);
        const segDistance = distance / (segLines - 1);
        const lineGroup = [];

        for (let i = 0; i < segLines; i++) {
          const groupLine = this.newLine(
            type,
            startPosition + segDistance * i,
            coordinate,
            startColor,
            startAlpha,
            l,
            isDefined(maxLength)
              ? toPixel(
                  maxLength,
                  type === 'horizontal' ? this.width : this.height,
                )
              : null,
          );
          lineContainer.push(groupLine);
          lineGroup.push(groupLine);
        }
        return { instructions: l, group: lineGroup };
      }

      const line = this.newLine(
        type,
        startPosition,
        coordinate,
        startColor,
        startAlpha,
        l,
        isDefined(maxLength)
          ? toPixel(maxLength, type === 'horizontal' ? this.width : this.height)
          : null,
      );
      lineContainer.push(line);

      return { line, instructions: l };
    });

    if (shouldReverse === -1) lines.reverse();

    // Calculate where lines draw + move to +  color/alpha controls
    const lineMovement = Promise.all([
      ...lines.map(({ line, group, instructions }, index) => {
        const lines = group ? group : [line];
        if (shouldReverse === -1) lines.reverse();
        let {
          drawToPosition,
          drawToLine,
          drawFromLine,
          drawFull,
          drawFullDelay = 0,
          endPosition,
          extend,
          drawDirection,
          endColor,
          endAlpha,
          drawStartLength,
          delay = 0,
          duration,
          removeAtEndPosition,
        } = instructions;

        let to = drawToPosition;
        let drawDelay = delay;
        let drawDuration = isDefined(duration) ? duration : this.duration;
        const p = [];

        // If a line will already have length at the start this property must be set to tell
        // it to draw from the start position and not from 0
        if (extend) {
          let sp = drawStartLength;
          let ep;
          let extendDelay = 0;
          let extendDuration = this.duration;
          let noDraw = false;
          if (!sp) {
            if (isDefined(drawToLine)) {
              const {
                startPosition: _sp,
                drawFullDelay: _drawFullDelay,
              } = this.getLineByIndex(perpendicularType, drawToLine);
              sp = _sp;

              if (isDefined(_drawFullDelay)) noDraw = true;
            } else {
              sp = drawToPosition;
            }

            if (isDefined(drawFromLine)) {
              const _l = this.getLineByIndex(perpendicularType, drawFromLine);
              ep = this.getEndPosition(_l, perpendicularType);

              const fromIndex = this.getIndexOfLine(
                perpendicularType,
                _l,
                shouldReverse,
              );

              let lineSpecificDelay = isDefined(_l.delay) ? _l.delay : 0;

              extendDuration = isDefined(_l.duration)
                ? _l.duration
                : this.duration;
              extendDelay = shouldDelayPerpendicular
                ? 100 * fromIndex + lineSpecificDelay
                : lineSpecificDelay;
            }
          }
          const total = type === 'horizontal' ? this.width : this.height;

          p.push(
            ...lines.map((line, index) => {
              return line.extend(
                sp,
                total,
                ep,
                this.onlyEnd ? 0 : extendDuration,
                this.onlyEnd ? 0 : extendDelay,
                noDraw,
              );
            }),
          );
        }

        if (isDefined(drawToLine)) {
          const d2l = this.getLineByIndex(perpendicularType, drawToLine);
          const { endPosition: ep, startPosition: sp } = d2l;

          to = isDefined(ep) ? ep : sp;
          const lineIndex = this.getIndexOfLine(
            perpendicularType,
            d2l,
            shouldReverse,
          );

          const lineSpecificDelay = isDefined(d2l.delay) ? d2l.delay : 0;

          if (isDefined(d2l.duration)) {
            drawDuration = d2l.duration;
          }

          if (isDefined(d2l.drawFullDelay)) {
            drawFullDelay = d2l.drawFullDelay;
          }

          drawDelay =
            shouldDelayPerpendicular && !extend
              ? 100 * lineIndex + lineSpecificDelay
              : lineSpecificDelay;
        }

        // Alpha
        if (isDefined(endAlpha) && !this.onlyStart) {
          p.push(
            ...lines.map(line =>
              line.animateAlpha(
                endAlpha,
                drawFull,
                this.onlyEnd ? 0 : duration,
                delay,
              ),
            ),
          );
        }

        // Color
        if (isDefined(endColor) && !this.onlyStart) {
          p.push(
            ...lines.map(line =>
              line.animateColor(
                endColor,
                drawFull,
                this.onlyEnd ? 0 : duration,
                delay,
              ),
            ),
          );
        }

        // Draw lines
        p.push(
          ...lines.map((line, index) => {
            if (this.onlyStart && !drawFull) return;
            return line.draw(
              {
                to: toPixel(
                  to,
                  type === 'horizontal' ? this.width : this.height,
                ),
              },
              drawFull || this.onlyEnd ? 1 : drawDuration,
              drawFull || this.onlyEnd || extend
                ? drawFullDelay
                : drawDelay + index * 100,
            );
          }),
        );

        // Reverse the draw
        if (drawFull && drawDirection === -1) {
          p.push(
            ...lines.map((line, index) => {
              line.toggleDirection();

              return line.draw(
                {
                  to: toPixel(
                    to,
                    type === 'horizontal' ? this.width : this.height,
                  ),
                },
                this.onlyEnd ? 0 : drawDuration,
                this.onlyEnd || !this.vDelay
                  ? drawFullDelay
                  : drawFullDelay + drawDelay + index * 100,
              );
            }),
          );
        }

        // Move
        if (isDefined(endPosition) && !this.onlyStart) {
          const moveDelay =
            (shouldDelay ? 100 * index + delay : delay) + drawFullDelay;

          if (type === 'horizontal')
            p.push(
              ...lines.map(line => {
                const mp = line.move(
                  { y: toPixel(endPosition, this.height) },
                  this.onlyEnd ? 0 : moveDelay,
                  this.onlyEnd ? 0 : duration,
                );

                if (removeAtEndPosition) {
                  return new Promise(async resolve => {
                    await mp;
                    line.remove();
                    resolve();
                  });
                }
                return mp;
              }),
            );
          else
            p.push(
              ...lines.map(line => {
                const mp = line.move(
                  { x: toPixel(endPosition, this.width) },
                  this.onlyEnd ? 0 : moveDelay,
                  this.onlyEnd ? 0 : duration,
                );
                if (removeAtEndPosition) {
                  return new Promise(async resolve => {
                    await mp;
                    line.remove();
                    resolve();
                  });
                }
                return mp;
              }),
            );
        }
        return Promise.all(p);
      }),
    ]);
    this[`${type}InterLines`] = lineContainer;
    setTimeout(() => {
      this.removeLines(this[`${type}Lines`]);
      this[`${type}InterLines`] = [];
      this[`${type}Lines`] = lineContainer;
    }, this.delayDelete? 50: 0);

    return lineMovement;
  }

  async drawHorizontal() {
    return this.drawLines('horizontal');
  }

  async drawVertical() {
    return this.drawLines('vertical');
  }

  calculateCircleRadius(instructions, start = true) {
    const {
      startPositionX,
      startPositionY,
      startLinePositionX,
      startLinePositionY,
      startBoundaryX,
      startBoundaryY,
      endBoundaryX,
      endBoundaryY,
      startBoundaryLineX,
      startBoundaryLineY,
      endBoundaryLineX,
      endBoundaryLineY,
    } = instructions;
    let radius = 0;

    // Calculate radius by position property
    if (
      [startBoundaryX, startBoundaryY, endBoundaryX, endBoundaryY].some(value =>
        isDefined(value),
      )
    ) {
      // Boundary set by X position
      if (isDefined(endBoundaryX) || isDefined(startBoundaryX)) {
        // Start Gets priority
        if (start) {
          radius = toPixel(
            isDefined(startBoundaryX) ? startBoundaryX : endBoundaryX,
            this.width,
          );
        } else {
          //End get priority
          radius = toPixel(
            isDefined(endBoundaryX) ? endBoundaryX : startBoundaryX,
            this.width,
          );
        }
      } else {
        // Boundary set by Y position
        if (start) {
          // Start Gets priority
          radius = toPixel(
            isDefined(startBoundaryY) ? startBoundaryY : endBoundaryY,
            this.height,
          );
        } else {
          // End Gets priority
          radius = toPixel(
            isDefined(endBoundaryY) ? endBoundaryY : startBoundaryY,
            this.height,
          );
        }
      }
    }

    // Calculate radius by line position
    if (
      [
        startBoundaryLineX,
        startBoundaryLineY,
        endBoundaryLineX,
        endBoundaryLineY,
      ].some(value => isDefined(value))
    ) {
      let boundaryLine;

      // Boundary set by Vertical Line
      if (isDefined(endBoundaryLineX) || isDefined(startBoundaryLineX)) {
        if (start) {
          boundaryLine = isDefined(startBoundaryLineX)
            ? startBoundaryLineX
            : endBoundaryLineX;
        } else {
          boundaryLine = isDefined(startBoundaryLineX)
            ? startBoundaryLineX
            : endBoundaryLineX;
        }
        const line = this.getLineByIndex('vertical', boundaryLine);
        let posX = startPositionX;
        if (isDefined(startLinePositionX)) {
          posX = this.getLineByIndex('vertical', startLinePositionX)
            .startPosition;
        }
        let position = Math.abs(line.startPosition - posX);

        if (!start && isDefined(line.endPosition)) {
          position = Math.abs(line.endPosition - posX);
        }

        radius = toPixel(position, this.width);
      }
      // Boundary set by horizontal line
      else {
        if (start) {
          boundaryLine = isDefined(startBoundaryLineY)
            ? startBoundaryLineY
            : endBoundaryLineY;
        } else {
          boundaryLine = isDefined(endBoundaryLineY)
            ? endBoundaryLineY
            : startBoundaryLineY;
        }
        const line = this.getLineByIndex('horizontal', boundaryLine);
        let posY = startPositionY;
        if (isDefined(startLinePositionX)) {
          posY = this.getLineByIndex('horizontal', startLinePositionY)
            .startPosition;
        }
        let position = Math.abs(line.startPosition - posY);

        if (!start && isDefined(line.endPosition)) {
          position = Math.abs(line.endPosition - posY);
        }

        radius = toPixel(position, this.height);
      }
    }
    return radius;
  }

  async drawCircle() {
    const c = this.sequence.circle || [];
    const cLinesContainer = [];

    const lines = c.map(l => {
      const {
        segmentGroup,
        dependentHorizontalLine,
        dependentVerticalLine,
      } = l;
      const radius = this.calculateCircleRadius(l, true);
      if (segmentGroup) {
        const {
          numLines,
          endSegmentLineX,
          endSegmentX,
          endSegmentLineY,
          endSegmentY,
        } = segmentGroup;

        const lineGroup = [];
        const segLines = numLines + 1;
        let radiusEnd = isDefined(endSegmentX)
          ? toPixel(endSegmentX, this.width)
          : toPixel(endSegmentY, this.height);

        if (isDefined(endSegmentLineX)) {
          radiusEnd = toPixel(
            this.getLineByIndex('vertical', endSegmentLineX).startPosition,
            this.width,
          );
        }

        if (isDefined(endSegmentLineY)) {
          radiusEnd = toPixel(
            this.getLineByIndex('horizontal', endSegmentLineY).startPosition,
            this.height,
          );
        }

        const distance = Math.abs(radius - radiusEnd);
        const segDistance = distance / segLines;

        for (let i = 1; i < segLines; i++) {
          const groupLine = this.newArcLine({
            ...l,
            radius: radius + segDistance,
          });
          lineGroup.push(groupLine);
          cLinesContainer.push(groupLine);
        }
        return {
          instructions: { ...l, groupRadius: segDistance },
          group: lineGroup,
        };
      }

      const line = this.newArcLine({ ...l, radius });

      if (isDefined(dependentHorizontalLine)) {
        line.setDependentLine(
          this.getLineByIndex('horizontal', dependentHorizontalLine, true),
          'horizontal',
          this.height,
          this.width,
        );
      }

      if (isDefined(dependentVerticalLine)) {
        line.setDependentLine(
          this.getLineByIndex('vertical', dependentVerticalLine, true),
          'vertical',
          this.height,
          this.width,
        );
      }

      cLinesContainer.push(line);
      return { line, instructions: l };
    });

    const lineMovement = Promise.all([
      ...lines.map(({ line, group, instructions }) => {
        const lines = group ? group : [line];
        let {
          drawFull,
          drawAngle,
          drawDirection,
          collapse,
          endPositionX,
          endPositionY,
          groupRadius = 0,
          delay = 0,
          duration = this.duration,
          collapseAtEnd,
          collapseToAngle = drawAngle,
          collapseDuration,
          maxAngle = drawAngle,
          endColor, // TODO
          endAlpha, // TODO
        } = instructions;

        const radius = this.calculateCircleRadius(instructions, this.onlyStart);
        const p = [
          ...lines.map(async (line, index) => {
            if (this.onlyStart && !drawFull) return;
            const p = line.draw(
              {
                to: drawAngle,
                radius: drawFull
                  ? line.lineData.radius
                  : radius + groupRadius * index,
              },
              drawFull || this.onlyEnd ? 0 : duration,
              this.onlyEnd ? 0 : delay + 100 * index,
            );

            if (collapseAtEnd) {
              await p;
              line.toggleDirection();
              line.lineData.to = drawAngle - maxAngle;
              const _duration = isDefined(collapseDuration)
                ? collapseDuration
                : duration;
              return line.draw(
                { to: collapseToAngle },
                this.onlyEnd ? 0 : _duration,
              );
            }

            return p;
          }),
        ];

        if (drawFull && radius) {
          p.push(
            ...lines.map((line, index) => {
              line.lineData.to = drawAngle;
              return line.draw(
                { radius: radius + groupRadius * index },
                this.onlyEnd || line.lineData.radius === radius ? 0 : duration,
              );
            }),
          );
        }

        if (collapse && !this.onlyStart) {
          p.push(
            ...lines.map((line, index) => {
              if (drawDirection === -1) {
                line.toggleDirection();
                line.lineData.to = 0;
                return line.draw(
                  { to: collapseToAngle },
                  this.onlyEnd ? 0 : duration,
                  this.onlyEnd ? 0 : 100 * index,
                );
              }
              const to = drawAngle !== collapseToAngle ? collapseToAngle : 0;
              line.lineData.to = drawAngle;
              return line.draw(
                { to: to },
                this.onlyEnd ? 0 : duration,
                this.onlyEnd ? 0 : delay + 100 * index,
              );
            }),
          );
        }

        if (isDefined(endPositionY) && !this.onlyStart) {
          p.push(
            ...lines.map(line =>
              line.move(
                { y: toPixel(endPositionY, this.height) },
                0,
                this.onlyEnd ? 0 : duration,
              ),
            ),
          );
        }

        if (isDefined(endPositionX) && !this.onlyStart) {
          p.push(
            ...lines.map(line =>
              line.move(
                { x: toPixel(endPositionX, this.width) },
                0,
                this.onlyEnd ? 0 : duration,
              ),
            ),
          );
        }

        return Promise.all(p);
      }),
    ]);

    this.circleInterLines = cLinesContainer;

    setTimeout(() => {
      this.removeLines(this.circleLines);
      this.circleLines = cLinesContainer;
      this.circleInterLines = [];
    }, this.delayDelete? 50: 0);

    return lineMovement;
  }

  getLinesByOrder() {
    const vertical = this.sorting.sortLines(
      this.sequence.vertical.filter(line => !line.segmentGroup),
      'vertical',
    );

    const horizontal = this.sorting.sortLines(
      this.sequence.horizontal.filter(line => !line.segmentGroup),
      'horizontal',
    );

    return { vertical, horizontal, circle: this.sequence.circle };
  }

  getEndPosition({ endPosition, endLinePosition, startPosition }, type) {
    const total = type === 'horizontal' ? this.height : this.width;

    if (isDefined(endPosition)) return toPixel(endPosition, total);
    if (isDefined(endLinePosition)) {
      this.getEndPosition(
        this.sortedLines[perpendicular(type)],
        perpendicular(type),
      );
    }
    return toPixel(startPosition, total);
  }

  getIndexOfLine(type, line, shouldReverse) {
    if (shouldReverse === -1) {
      const perpendicularLineCount = this.sequence[type].length;
      return perpendicularLineCount - 1 - this.sortedLines[type].indexOf(line);
    }
    return this.sortedLines[type].indexOf(line);
  }

  getLineByIndex(type, lineIndex, getClass = false) {
    if (typeof lineIndex === 'string') {
      const line = this.sortedLines[type].find(l => l.name === lineIndex);

      if (!isDefined(line))
        throw new Error(`No ${type} line found with name ${lineIndex}`);

      if (getClass) {
        if (this[`${type}InterLines`].length > 0) {
          return this[`${type}InterLines`].find(
            l => l.instructions.name === lineIndex,
          );
        }

        return this[`${type}Lines`].find(
          l => l.instructions.name === lineIndex,
        );
      }

      return line;
    }

    const line = this.sortedLines[type][lineIndex];

    if (getClass) {
      if (this[`${type}InterLines`].length > 0) {
        return this[`${type}InterLines`].find(l =>
          isEqual(l.instructions, line),
        );
      }

      return this[`${type}Lines`].find(l => isEqual(l.instructions, line));
    }
    return line;
  }
}
